import React, { useState, useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import DashboardLayout from "../components/dashboard/DashboardLayout";
import { Card } from "../components/ui/Card";
import Badge from "../components/ui/Badge";
import Button from "../components/common/Button";
import Input from "../components/common/Input";
import Modal from "../components/common/Modal";
import LoadingSpinner from "../components/common/LoadingSpinner";
import Toast from "../components/common/Toast";
import Tabs from "../components/ui/Tabs";
import TransactionHistory from "../components/dashboard/TransactionHistory";
import { formatCurrency, formatNumber, formatDate } from "../utils/formatters";
import {
  validateTradeAmount,
} from "../utils/validators";
import {
  Wallet as WalletIcon,
  CreditCard,
  ArrowLeft,
  ArrowDownToLine,
  ArrowUpFromLine,
  CheckCircle,
  XCircle,
  AlertCircle,
  Copy,
  QrCode,
  Building2,
  DollarSign,
  RefreshCw,
  Filter,
  Plus,
  ArrowUpRight,
  ArrowDownRight,
  History,
  Eye,
  EyeOff,
  TrendingDown,
} from "lucide-react";
import { useTrading } from "../context/TradingContext";

const WALLET_TRANSACTIONS_STORAGE_KEY = "gainwise-wallet-transactions";
const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
const getWalletTransactionsStorageKey = (userId) =>
  `${WALLET_TRANSACTIONS_STORAGE_KEY}:${userId || "guest"}`;
const DEPOSIT_WALLET_FALLBACKS = [
  {
    id: "bitcoin",
    label: "Bitcoin",
    symbol: "BTC",
    network: "Bitcoin",
    address: "bc1q8g6x4k0m7w2h9r5p3t8y1n6v4c2z7u5e9a1d3f",
    qrImageUrl: null,
  },
  {
    id: "usdt-trc20",
    label: "USDT TRC20",
    symbol: "USDT",
    network: "TRC20",
    address: "TQ7w8u5L9v2g4m1n8r6p3x0c7b5k2h9d4s",
    qrImageUrl: null,
  },
  {
    id: "usdt-erc20",
    label: "USDT ERC20",
    symbol: "USDT",
    network: "ERC20",
    address: "0x8f4c2e7b1d9a6f3c5e2b8d4a7c1f9b6e3d5a2c7f",
    qrImageUrl: null,
  },
];
const DEPOSIT_WALLET_ENDPOINTS = [
  `${import.meta.env.VITE_BACKEND_API_BASE_URL || ""}/get-deposit-wallets.php`,
  `${import.meta.env.VITE_AUTH_API_BASE_URL || ""}/get-deposit-wallets.php`,
  "https://gainwisefx.com/api/get-deposit-wallets.php",
  "/api/get-deposit-wallets.php",
].filter((value, index, list) => value && list.indexOf(value) === index);
const WITHDRAWAL_FETCH_ENDPOINTS = ["/api/get-withdrawal-requests.php"];
const WITHDRAWAL_CREATE_ENDPOINTS = ["/api/create-withdrawal-request.php"];
const WITHDRAWAL_STATUS_ENDPOINTS = ["/api/update-withdrawal-request-status.php"];

const getWalletAuthHeaders = (user) => {
  const token =
    localStorage.getItem("authToken") ||
    localStorage.getItem("token") ||
    localStorage.getItem("trading_app_token") ||
    sessionStorage.getItem("authToken") ||
    sessionStorage.getItem("token") ||
    sessionStorage.getItem("trading_app_token") ||
    "";
  const headers = { "Content-Type": "application/json" };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
    headers["X-Auth-Token"] = token;
  }

  return headers;
};

const buildWalletApiError = (message, endpoint, status) => {
  const error = new Error(message);
  error.endpoint = endpoint;
  error.status = status;
  return error;
};

const parseWalletApiResponse = async (response, fallbackMessage, endpoint) => {
  const payload = await response.json().catch(() => null);

  if (!response.ok) {
    throw buildWalletApiError(
      payload?.message || payload?.error || fallbackMessage,
      endpoint,
      response.status
    );
  }

  if (!payload?.success) {
    throw buildWalletApiError(
      payload?.message || payload?.error || fallbackMessage,
      endpoint,
      response.status
    );
  }

  return payload;
};

const formatWalletRequestError = (error, fallbackMessage) => {
  if (!navigator.onLine) {
    return "No internet connection. Please check your network and try again.";
  }

  if (error?.message) {
    return error.message;
  }

  return fallbackMessage;
};

const normalizeWithdrawalAddress = (value) =>
  String(value || "").trim().toLowerCase();

const isSameWithdrawalRequest = (withdrawal, payload) => {
  const createdAt = new Date(
    withdrawal?.createdAt || withdrawal?.updatedAt || withdrawal?.timestamp || 0
  ).getTime();
  const now = Date.now();

  return (
    String(withdrawal?.assetId || "").toLowerCase() ===
      String(payload?.assetId || "").toLowerCase() &&
    String(withdrawal?.currency || "").toUpperCase() ===
      String(payload?.currency || "").toUpperCase() &&
    String(withdrawal?.network || "").toUpperCase() ===
      String(payload?.network || "").toUpperCase() &&
    normalizeWithdrawalAddress(withdrawal?.address) ===
      normalizeWithdrawalAddress(payload?.address) &&
    Number(withdrawal?.amount || 0).toFixed(2) ===
      Number(payload?.amount || 0).toFixed(2) &&
    Number.isFinite(createdAt) &&
    Math.abs(now - createdAt) <= 5 * 60 * 1000
  );
};

const recoverSavedWithdrawalRequest = async (user, payload) => {
  for (const endpoint of WITHDRAWAL_FETCH_ENDPOINTS) {
    try {
      const response = await fetch(endpoint, {
        method: "GET",
        headers: getWalletAuthHeaders(user),
      });

      const apiPayload = await parseWalletApiResponse(
        response,
        "Failed to load withdrawal requests",
        endpoint
      );

      const matchingWithdrawal = Array.isArray(apiPayload?.withdrawals)
        ? apiPayload.withdrawals.find((withdrawal) =>
            isSameWithdrawalRequest(withdrawal, payload)
          )
        : null;

      if (matchingWithdrawal) {
        return {
          success: true,
          withdrawal: matchingWithdrawal,
          account: apiPayload?.account || null,
        };
      }
    } catch {
      continue;
    }
  }

  return null;
};

const createLocalMockWithdrawal = ({
  asset,
  address,
  amount,
  portfolioBalance,
  account,
}) => {
  const createdAt = new Date().toISOString();
  const currentBalance = Math.max(
    0,
    Number((portfolioBalance - amount).toFixed(2))
  );
  const baseAccount = account || {};

  return {
    withdrawal: {
      id: `mock-${Date.now()}`,
      assetId: asset.id,
      currency: asset.symbol,
      network: asset.network,
      address,
      amount,
      networkFee: "Network fee",
      status: "pending",
      createdAt,
      updatedAt: createdAt,
    },
    account: {
      startingBalance: parseNumericBalance(baseAccount.startingBalance),
      currentBalance,
      equity: currentBalance,
      availableBalance: currentBalance,
      lockedBalance: parseNumericBalance(baseAccount.lockedBalance) + amount,
      totalPnL: parseNumericBalance(baseAccount.totalPnL),
      realizedPnL: parseNumericBalance(baseAccount.realizedPnL),
      totalInvested: parseNumericBalance(baseAccount.totalInvested),
      completedTrades: Number(baseAccount.completedTrades || 0),
      winningTrades: Number(baseAccount.winningTrades || 0),
      losses: Number(baseAccount.losses || 0),
      updatedAt: createdAt,
    },
    isLocalFallback: true,
  };
};

const createMockWithdrawalRequest = async (user, payload) => {
  const requestErrors = [];

  for (const endpoint of WITHDRAWAL_CREATE_ENDPOINTS) {
    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: getWalletAuthHeaders(user),
        body: JSON.stringify(payload),
      });

      return await parseWalletApiResponse(
        response,
        "Failed to create withdrawal request",
        endpoint
      );
    } catch (error) {
      requestErrors.push(error);
      continue;
    }
  }

  const recoveredResult = await recoverSavedWithdrawalRequest(user, payload);
  if (recoveredResult?.withdrawal) {
    return recoveredResult;
  }

  throw (
    requestErrors.find((error) => error?.status && error.status < 500) ||
    requestErrors[requestErrors.length - 1] ||
    new Error("Failed to create withdrawal request")
  );
};

const getWithdrawalDisplayStatus = (status) => {
  const normalizedStatus = String(status || "").toLowerCase();

  if (normalizedStatus === "pending") {
    return "request accepted";
  }

  return normalizedStatus;
};

const getWithdrawalEventKey = (withdrawal) => {
  if (withdrawal?.id && !String(withdrawal.id).startsWith("mock-")) {
    return `id:${withdrawal.id}`;
  }

  const timestamp = new Date(
    withdrawal.createdAt || withdrawal.updatedAt || withdrawal.timestamp || Date.now()
  ).getTime();
  const timeBucket = Number.isFinite(timestamp) ? Math.floor(timestamp / 60000) : 0;

  return [
    String(withdrawal.currency || "").toUpperCase(),
    String(withdrawal.network || "").toUpperCase(),
    String(withdrawal.address || "").trim().toLowerCase(),
    Number(withdrawal.amount || 0).toFixed(2),
    timeBucket,
  ].join(":");
};

const mapWithdrawalToSharedTransaction = (withdrawal) => ({
  id: `withdrawal-${getWithdrawalEventKey(withdrawal)}`,
  symbol: withdrawal.currency,
  side: "withdrawal",
  status: getWithdrawalDisplayStatus(withdrawal.status),
  quantity: null,
  price: Number(withdrawal.amount || 0),
  profit: 0,
  amount: Number(withdrawal.amount || 0),
  investedAmount: 0,
  source: withdrawal.network,
  timestamp: withdrawal.createdAt || withdrawal.timestamp || new Date().toISOString(),
  description: `${withdrawal.currency} withdrawal ${withdrawal.status}`,
  address: withdrawal.address,
  network: withdrawal.network,
  transactionType: "withdrawal",
});

const mapWithdrawalToWalletTransaction = (withdrawal) => ({
  id: `server-withdrawal-${getWithdrawalEventKey(withdrawal)}`,
  type: "withdrawal",
  currency: withdrawal.currency,
  symbol: withdrawal.currency,
  side: "withdrawal",
  quantity: null,
  price: Number(withdrawal.amount || 0),
  profit: 0,
  source: withdrawal.network,
  amount: Number(withdrawal.amount || 0),
  status: getWithdrawalDisplayStatus(withdrawal.status),
  method: "crypto",
  fee: withdrawal.networkFee ?? "Network fee",
  timestamp: new Date(withdrawal.createdAt || withdrawal.timestamp || Date.now()),
  txHash: null,
  description: `${withdrawal.currency} withdrawal to ${withdrawal.network}`,
  address: withdrawal.address,
  network: withdrawal.network,
});

const mergeUniqueTransactions = (transactions) => {
  const seen = new Set();

  return transactions.filter((transaction) => {
    const key = String(transaction.id);

    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
};

const pruneWalletTransactions = (transactions) => {
  const cutoff = Date.now() - THIRTY_DAYS_MS;
  return transactions.filter((transaction) => {
    const timestamp = new Date(transaction.timestamp || Date.now()).getTime();
    return Number.isFinite(timestamp) && timestamp >= cutoff;
  });
};

const getMatchingWithdrawOptionId = (options, currency) => {
  const normalizedCurrency = String(currency || "").toUpperCase();
  const exactMatch = options.find(
    (option) => String(option.symbol || "").toUpperCase() === normalizedCurrency
  );

  return exactMatch?.id || options[0]?.id || "bitcoin";
};

const parseNumericBalance = (value) => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }

  if (typeof value === "string") {
    const normalized = value.replace(/[^0-9.-]/g, "");
    const parsed = Number.parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  return 0;
};

const getTimestampValue = (value) => {
  const timestamp = new Date(value || 0).getTime();
  return Number.isFinite(timestamp) ? timestamp : 0;
};

const isWithdrawalTransaction = (transaction) =>
  String(transaction?.type || "").toLowerCase() === "withdrawal" ||
  String(transaction?.side || "").toLowerCase() === "withdrawal" ||
  String(transaction?.transactionType || "").toLowerCase() === "withdrawal";

const getWithdrawalStatusRank = (status) => {
  const normalizedStatus = String(status || "").toLowerCase();

  if (normalizedStatus === "approved") {
    return 3;
  }

  if (normalizedStatus === "failed" || normalizedStatus === "rejected") {
    return 2;
  }

  if (normalizedStatus === "request accepted" || normalizedStatus === "pending") {
    return 1;
  }

  return 0;
};

const getWithdrawalTransactionSignature = (transaction) =>
  [
    String(transaction?.symbol || transaction?.currency || "").toUpperCase(),
    String(transaction?.network || transaction?.source || "").toUpperCase(),
    String(transaction?.address || "").trim().toLowerCase(),
    Number(transaction?.amount ?? transaction?.price ?? 0).toFixed(2),
  ].join(":");

const dedupeWithdrawalTransactions = (transactions) => {
  const result = [];
  const withdrawalIndexBySignature = new Map();

  transactions.forEach((transaction) => {
    if (!isWithdrawalTransaction(transaction)) {
      result.push(transaction);
      return;
    }

    const signature = getWithdrawalTransactionSignature(transaction);
    const existingIndex = withdrawalIndexBySignature.get(signature);

    if (existingIndex === undefined) {
      withdrawalIndexBySignature.set(signature, result.length);
      result.push(transaction);
      return;
    }

    const existingTransaction = result[existingIndex];
    const existingRank = getWithdrawalStatusRank(existingTransaction.status);
    const nextRank = getWithdrawalStatusRank(transaction.status);
    const existingTimestamp = getTimestampValue(existingTransaction.timestamp);
    const nextTimestamp = getTimestampValue(transaction.timestamp);

    if (
      nextRank > existingRank ||
      (nextRank === existingRank && nextTimestamp >= existingTimestamp)
    ) {
      result[existingIndex] = {
        ...existingTransaction,
        ...transaction,
        ...(nextRank >= existingRank ? { status: transaction.status } : {}),
        timestamp:
          nextTimestamp >= existingTimestamp
            ? transaction.timestamp
            : existingTransaction.timestamp,
      };
    }
  });

  return result;
};

const Wallet = () => {
  const { user } = useAuth();
  const location = useLocation();
  const {
    account,
    portfolio,
    transactions: tradingTransactions,
    addTransaction,
    updateTransaction,
    syncAccountState,
    persistAccountState,
  } = useTrading();
  const virtualPortfolioBalance = Number(
    Math.max(
      0,
      parseNumericBalance(portfolio?.totalValue),
      parseNumericBalance(account?.currentBalance),
      parseNumericBalance(account?.equity),
      parseNumericBalance(account?.availableBalance)
    ).toFixed(2)
  );

  // State Management
  const [activeTab, setActiveTab] = useState("overview");
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState("USD");
  const [depositMethod, setDepositMethod] = useState("bank");
  const [withdrawMethod, setWithdrawMethod] = useState("bank");
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [hideBalances, setHideBalances] = useState(false);

  // Form states
  const [depositAmount, setDepositAmount] = useState("");
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [withdrawAddress, setWithdrawAddress] = useState("");
  const [bankAccount, setBankAccount] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [selectedDepositOption, setSelectedDepositOption] = useState("bitcoin");
  const [selectedWithdrawOption, setSelectedWithdrawOption] = useState("bitcoin");
  const [depositOptions, setDepositOptions] = useState(DEPOSIT_WALLET_FALLBACKS);
  const [withdrawalFlowStatus, setWithdrawalFlowStatus] = useState("idle");
  const [withdrawalRequests, setWithdrawalRequests] = useState([]);
  const withdrawalSubmitLockRef = useRef(false);
  const withdrawalToastTimeoutRef = useRef(null);
  const lastWithdrawalSuccessToastAtRef = useRef(0);
  const activeWithdrawalActionKeyRef = useRef(null);

  const activeDepositOption =
    depositOptions.find((option) => option.id === selectedDepositOption) ||
    depositOptions[0];
  const activeWithdrawOption =
    depositOptions.find((option) => option.id === selectedWithdrawOption) ||
    depositOptions[0];
  const activeWithdrawSymbol = activeWithdrawOption?.symbol || selectedCurrency;

  useEffect(() => {
    if (!depositOptions.some((option) => option.id === selectedDepositOption) && depositOptions[0]) {
      setSelectedDepositOption(depositOptions[0].id);
    }

    if (!depositOptions.some((option) => option.id === selectedWithdrawOption) && depositOptions[0]) {
      setSelectedWithdrawOption(depositOptions[0].id);
    }

    if (
      depositOptions.length &&
      !depositOptions.some((option) => option.symbol === selectedCurrency)
    ) {
      setSelectedCurrency(depositOptions[0].symbol);
    }
  }, [depositOptions, selectedCurrency, selectedDepositOption, selectedWithdrawOption]);

  const walletData = useMemo(() => ({
    totalBalance: virtualPortfolioBalance,
    availableBalance: virtualPortfolioBalance,
    lockedBalance: account.lockedBalance,
    balances: [
      {
        currency: "USD",
        symbol: "$",
        balance: virtualPortfolioBalance,
        available: virtualPortfolioBalance,
        locked: account.lockedBalance,
        value: virtualPortfolioBalance,
        change24h: account.totalPnL,
        type: "fiat",
      },
      {
        currency: "BTC",
        symbol: "₿",
        balance: 0.85432,
        available: 0.75432,
        locked: 0.1,
        value: 29901.2,
        change24h: 2.4,
        type: "crypto",
      },
      {
        currency: "ETH",
        symbol: "Ξ",
        balance: 12.5432,
        available: 11.5432,
        locked: 1.0,
        value: 27598.4,
        change24h: 1.8,
        type: "crypto",
      },
      {
        currency: "USDT",
        symbol: "₮",
        balance: 5000.0,
        available: 5000.0,
        locked: 0.0,
        value: 5000.0,
        change24h: 0.1,
        type: "crypto",
      },
    ],
  }), [account, virtualPortfolioBalance]);

  const [transactions, setTransactions] = useState(() => {
    if (typeof window === "undefined" || !user?.id) {
      return [];
    }

    try {
      const savedTransactions = window.localStorage.getItem(
        getWalletTransactionsStorageKey(user.id)
      );

      if (!savedTransactions) {
        return [];
      }

      return pruneWalletTransactions(
        JSON.parse(savedTransactions).map((transaction) => ({
          ...transaction,
          timestamp: new Date(transaction.timestamp),
        }))
      );
    } catch (error) {
      return [];
    }
  });

  const recentTransactions = useMemo(
    () =>
      dedupeWithdrawalTransactions([...transactions])
        .sort(
          (left, right) =>
            new Date(right.timestamp || 0).getTime() -
            new Date(left.timestamp || 0).getTime()
        )
        .slice(0, 5),
    [transactions]
  );

  useEffect(() => {
    if (typeof window === "undefined" || !user?.id) {
      setTransactions([]);
      return;
    }

    try {
      const savedTransactions = window.localStorage.getItem(
        getWalletTransactionsStorageKey(user.id)
      );

      if (!savedTransactions) {
        setTransactions([]);
        return;
      }

      setTransactions(
        pruneWalletTransactions(
          JSON.parse(savedTransactions).map((transaction) => ({
            ...transaction,
            timestamp: new Date(transaction.timestamp),
          }))
        )
      );
    } catch {
      setTransactions([]);
    }
  }, [user?.id]);

  useEffect(() => {
    if (!location.state?.openDepositModal) {
      return;
    }

    if (location.state.activeTab) {
      setActiveTab(location.state.activeTab);
    }

    setShowDepositModal(true);
    window.history.replaceState(
      {},
      document.title,
      window.location.pathname + window.location.search
    );
  }, [location.pathname, location.search, location.state]);

  useEffect(() => {
    let cancelled = false;

    const loadDepositWallets = async () => {
      const requestErrors = [];

      for (const endpoint of DEPOSIT_WALLET_ENDPOINTS) {
        try {
          const response = await fetch(endpoint);
          const payload = await parseWalletApiResponse(
            response,
            "Failed to load deposit wallet options",
            endpoint
          );
          if (!payload?.success || !Array.isArray(payload.wallets) || !payload.wallets.length) {
            continue;
          }

          if (!cancelled) {
            setDepositOptions(
              payload.wallets.map((wallet) => ({
                ...wallet,
                qrImageUrl: wallet.qrImageUrl || null,
              }))
            );
          }
          return;
        } catch (error) {
          requestErrors.push(error);
          continue;
        }
      }

      if (!cancelled && requestErrors.length) {
        setToast({
          type: "error",
          message: formatWalletRequestError(
            requestErrors[requestErrors.length - 1],
            "Unable to load deposit wallet options."
          ),
        });
      }
    };

    loadDepositWallets();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const syncWithdrawals = async () => {
      for (const endpoint of WITHDRAWAL_FETCH_ENDPOINTS) {
        try {
          const response = await fetch(endpoint, {
            method: "GET",
            headers: getWalletAuthHeaders(user),
          });

          if (!response.ok) {
            continue;
          }

          const payload = await response.json();
          if (!payload?.success || !Array.isArray(payload.withdrawals)) {
            continue;
          }

          if (cancelled) {
            return;
          }

          setWithdrawalRequests(payload.withdrawals);

          if (payload.account) {
            const remoteTimestamp = getTimestampValue(payload.account.updatedAt);
            const localTimestamp = getTimestampValue(account?.updatedAt);

            if (remoteTimestamp >= localTimestamp) {
              syncAccountState(payload.account);
            }
          }

          payload.withdrawals.forEach((withdrawal) => {
            const sharedTransaction = mapWithdrawalToSharedTransaction(withdrawal);
            updateTransaction(sharedTransaction.id, sharedTransaction);
          });

          setTransactions((previous) => {
            const nonServerWithdrawals = previous.filter(
              (transaction) => !String(transaction.id).startsWith("server-withdrawal-")
            );
            const backendWithdrawals = payload.withdrawals.map(mapWithdrawalToWalletTransaction);
            return pruneWalletTransactions(
              dedupeWithdrawalTransactions(
                mergeUniqueTransactions([
                  ...backendWithdrawals,
                  ...nonServerWithdrawals,
                ])
              )
            );
          });
          return;
        } catch {
          continue;
        }
      }
    };

    syncWithdrawals();
    const intervalId = window.setInterval(syncWithdrawals, 15000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [account?.updatedAt, syncAccountState, updateTransaction, user]);

  useEffect(() => {
    const nonWithdrawalTradingTransactions = tradingTransactions.filter(
      (transaction) =>
        !(
          String(transaction.transactionType || "").toLowerCase() === "withdrawal" ||
          String(transaction.side || "").toLowerCase() === "withdrawal" ||
          String(transaction.type || "").toLowerCase() === "withdrawal"
        )
    );

    if (!nonWithdrawalTradingTransactions.length) {
      return;
    }

    const syncedTransactions = nonWithdrawalTradingTransactions.map((transaction) => ({
      id: `trade-sync-${transaction.id}`,
      type:
        String(transaction.transactionType || "").toLowerCase() === "withdrawal" ||
        String(transaction.side || "").toLowerCase() === "withdrawal" ||
        String(transaction.type || "").toLowerCase() === "withdrawal" ||
        String(transaction.side || "").toLowerCase() === "sell"
          ? "withdrawal"
          : "deposit",
      currency: transaction.symbol || "USD",
      symbol: transaction.symbol || "USD",
      side: transaction.side || transaction.type || "trade",
      quantity: transaction.quantity,
      price: transaction.price,
      profit: transaction.profit,
      source: transaction.source,
      amount:
        transaction.notional ||
        transaction.amount ||
        transaction.investedAmount ||
        transaction.price ||
        0,
      status: transaction.status || "completed",
      method: "trade",
      fee: 0,
      timestamp: new Date(transaction.timestamp || Date.now()),
      txHash: null,
      description: `${transaction.side || "trade"} ${transaction.symbol || "USD"} execution`,
    }));

    setTransactions((previous) => {
      const manualTransactions = previous.filter(
        (transaction) =>
          !String(transaction.id).startsWith("trade-sync-") &&
          !String(transaction.id).startsWith("server-withdrawal-")
      );
      const backendWithdrawals = withdrawalRequests.map(mapWithdrawalToWalletTransaction);
      return pruneWalletTransactions([
        ...dedupeWithdrawalTransactions(
          mergeUniqueTransactions([
            ...backendWithdrawals,
            ...syncedTransactions,
            ...manualTransactions,
          ])
        ),
      ]);
    });
  }, [tradingTransactions, withdrawalRequests]);

  useEffect(() => {
    if (typeof window === "undefined" || !user?.id) {
      return;
    }

    window.localStorage.setItem(
      getWalletTransactionsStorageKey(user.id),
      JSON.stringify(pruneWalletTransactions(transactions))
    );
  }, [transactions, user?.id]);

  // Tab configuration
  const tabsData = [
    { id: "overview", label: "Overview", icon: WalletIcon },
    { id: "deposit", label: "Deposit", icon: ArrowDownToLine },
    { id: "withdraw", label: "Withdraw", icon: ArrowUpFromLine },
    {
      id: "history",
      label: "History",
      icon: History,
      count: transactions.length,
    },
  ];

  // Payment methods
  const depositMethods = [
    {
      id: "bank",
      label: "Bank Transfer",
      icon: Building2,
      fee: "0%",
      time: "1-3 business days",
    },
    {
      id: "card",
      label: "Credit/Debit Card",
      icon: CreditCard,
      fee: "2.5%",
      time: "Instant",
    },
    {
      id: "crypto",
      label: "Cryptocurrency",
      icon: DollarSign,
      fee: "Network fee",
      time: "10-60 mins",
    },
  ];

  const withdrawMethods = [
    {
      id: "bank",
      label: "Bank Transfer",
      icon: Building2,
      fee: "Network fee",
      time: "1-3 business days",
    },
    {
      id: "crypto",
      label: "Cryptocurrency",
      icon: DollarSign,
      fee: "Network fee",
      time: "10-60 mins",
    },
  ];

  // Event handlers
  const handleDeposit = async () => {
    if (validateTradeAmount(depositAmount)) {
      setToast({ type: "error", message: validateTradeAmount(depositAmount) });
      return;
    }

    setLoading(true);
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 2000));

      const newTransaction = {
        id: String(transactions.length + 1),
        userId: user?.id || null,
        type: "deposit",
        currency: selectedCurrency,
        amount: parseFloat(depositAmount),
        status: "pending",
        method: depositMethod,
        fee: depositMethod === "card" ? parseFloat(depositAmount) * 0.025 : 0,
        timestamp: new Date(),
        txHash:
          depositMethod === "crypto"
            ? "0x" + Math.random().toString(16).substr(2, 40)
            : null,
        description: `${
          depositMethod === "bank"
            ? "Bank"
            : depositMethod === "card"
            ? "Card"
            : "Crypto"
        } deposit`,
      };

      setTransactions((prev) => [newTransaction, ...prev]);

      setToast({
        type: "success",
        message: `Deposit of ${formatCurrency(
          parseFloat(depositAmount)
        )} initiated successfully`,
      });

      setShowDepositModal(false);
      setDepositAmount("");
    } catch (error) {
      setToast({ type: "error", message: "Failed to process deposit" });
    } finally {
      setLoading(false);
    }
  };

  const handleWithdraw = async () => {
    if (withdrawalSubmitLockRef.current || withdrawalFlowStatus !== "idle") {
      return;
    }

    const selectedAsset = activeWithdrawOption;

    if (!selectedAsset) {
      setToast({
        type: "error",
        message: "Select a valid withdrawal asset before continuing",
      });
      return;
    }

    const rawWithdrawalAmount = String(withdrawAmount || "");
    if (!rawWithdrawalAmount.trim()) {
      setToast({
        type: "error",
        message: "Enter withdrawal amount",
      });
      return;
    }

    const amount = Number.parseFloat(rawWithdrawalAmount.replace(/[^0-9.]/g, ""));
    if (!Number.isFinite(amount) || amount <= 0) {
      setToast({
        type: "error",
        message: "Enter withdrawal amount",
      });
      return;
    }

    if (!withdrawAddress.trim()) {
      setToast({
        type: "error",
        message: "Withdrawal address is required",
      });
      return;
    }

    const displayedVirtualUsdBalance = parseNumericBalance(portfolio?.totalValue);
    const effectiveVirtualBalance = Number(
      Math.max(displayedVirtualUsdBalance, virtualPortfolioBalance).toFixed(2)
    );

    if (amount > effectiveVirtualBalance) {
      setToast({
        type: "error",
        message: "Insufficient balance",
      });
      return;
    }

    const withdrawalActionKey = [
      selectedAsset.id,
      selectedAsset.network,
      withdrawAddress.trim().toLowerCase(),
      amount.toFixed(2),
    ].join(":");

    if (activeWithdrawalActionKeyRef.current === withdrawalActionKey) {
      return;
    }

    activeWithdrawalActionKeyRef.current = withdrawalActionKey;
    withdrawalSubmitLockRef.current = true;
    setLoading(true);

    try {
      const requestPayload = {
        assetId: selectedAsset.id,
        currency: selectedAsset.symbol,
        network: selectedAsset.network,
        address: withdrawAddress.trim(),
        amount,
        clientPortfolioBalance: effectiveVirtualBalance,
        clientRequestId:
          (typeof crypto !== "undefined" && crypto.randomUUID
            ? crypto.randomUUID()
            : `wd-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`),
      };

      const createResult = await createMockWithdrawalRequest(user, requestPayload);

      const createdWithdrawal = createResult.withdrawal;
      const nextAccountSnapshot = createResult.account;

      const walletTransaction = mapWithdrawalToWalletTransaction(createdWithdrawal);
      const sharedTransaction = mapWithdrawalToSharedTransaction(createdWithdrawal);

      setWithdrawalFlowStatus("processing");
      setTransactions((previous) =>
        dedupeWithdrawalTransactions(
          pruneWalletTransactions(
            mergeUniqueTransactions([walletTransaction, ...previous])
          )
        )
      );
      updateTransaction(sharedTransaction.id, sharedTransaction);
      syncAccountState(nextAccountSnapshot);
      void persistAccountState(nextAccountSnapshot);
      setWithdrawalRequests((previous) => [
        createdWithdrawal,
        ...previous.filter(
          (withdrawal) => String(withdrawal.id) !== String(createdWithdrawal.id)
        ),
      ]);

      await new Promise((resolve) => window.setTimeout(resolve, 1400));
      const approvedWithdrawal = {
        ...createdWithdrawal,
        status: "request accepted",
        adminNote: "Mock withdrawal request accepted",
        updatedAt: new Date().toISOString(),
      };

      setWithdrawalFlowStatus("approved");

      window.clearTimeout(withdrawalToastTimeoutRef.current);
      withdrawalToastTimeoutRef.current = window.setTimeout(() => {
        setShowWithdrawModal(false);
        setWithdrawAmount("");
        setWithdrawAddress("");
        setWithdrawalFlowStatus("idle");
        withdrawalSubmitLockRef.current = false;
        activeWithdrawalActionKeyRef.current = null;
        const now = Date.now();
        if (now - lastWithdrawalSuccessToastAtRef.current > 3000) {
          lastWithdrawalSuccessToastAtRef.current = now;
          setToast({
            type: "success",
            message: "Withdrawal successful",
          });
        }
      }, 900);
    } catch (error) {
      setWithdrawalFlowStatus("idle");
      withdrawalSubmitLockRef.current = false;
      activeWithdrawalActionKeyRef.current = null;
      setToast({
        type: "error",
        message: formatWalletRequestError(
          error,
          "Unable to create withdrawal request"
        ),
      });
    } finally {
      setLoading(false);
    }
  };

  const openWithdrawConfirmation = () => {
    if (withdrawMethod !== "crypto") {
      setToast({
        type: "error",
        message: "Select a cryptocurrency withdrawal network to continue",
      });
      return;
    }

    if (depositOptions.length) {
      setSelectedWithdrawOption(
        getMatchingWithdrawOptionId(depositOptions, selectedCurrency)
      );
    }

    setShowWithdrawModal(true);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setToast({ type: "success", message: "Copied to clipboard" });
  };

  const handleRefresh = async () => {
    setLoading(true);
    try {
      // Simulate API refresh
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setToast({ type: "success", message: "Wallet data refreshed" });
    } catch (error) {
      setToast({ type: "error", message: "Failed to refresh data" });
    } finally {
      setLoading(false);
    }
  };

  // Render methods
  const renderOverview = () => (
    <div className="space-y-6">
      <div className="grid gap-6 xl:grid-cols-[1.55fr_0.95fr]">
        <Card className="overflow-hidden border-0 bg-gradient-to-br from-slate-950 via-slate-900 to-cyan-950 p-0 text-white shadow-2xl shadow-cyan-950/20">
          <div className="relative p-7">
            <div className="absolute -right-16 -top-16 h-44 w-44 rounded-full bg-cyan-400/10 blur-3xl" />
            <div className="absolute -bottom-12 left-20 h-36 w-36 rounded-full bg-blue-500/10 blur-3xl" />
            <div className="relative flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <Badge className="border-cyan-400/30 bg-cyan-400/10 text-cyan-100">
                  Wallet Overview
                </Badge>
                <p className="mt-4 text-sm uppercase tracking-[0.24em] text-slate-400">
                  Total Balance
                </p>
                <p className="mt-2 text-4xl font-semibold tracking-tight lg:text-5xl">
                  {hideBalances ? "****" : formatCurrency(walletData.totalBalance)}
                </p>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={() => setShowDepositModal(true)}
                  className="rounded-2xl border border-white/10 bg-white/5 p-4 text-left transition hover:bg-white/10"
                >
                  <ArrowDownToLine className="mb-3 h-5 w-5 text-cyan-300" />
                  <div className="text-sm font-medium">Deposit Funds</div>
                  <div className="mt-1 text-xs text-slate-400">
                    Add crypto into your wallet balance
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setShowWithdrawModal(true)}
                  className="rounded-2xl border border-white/10 bg-white/5 p-4 text-left transition hover:bg-white/10"
                >
                  <ArrowUpFromLine className="mb-3 h-5 w-5 text-rose-300" />
                  <div className="text-sm font-medium">Withdraw Funds</div>
                  <div className="mt-1 text-xs text-slate-400">
                    Move assets to an external wallet
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab("history")}
                  className="rounded-2xl border border-white/10 bg-white/5 p-4 text-left transition hover:bg-white/10"
                >
                  <History className="mb-3 h-5 w-5 text-amber-300" />
                  <div className="text-sm font-medium">View History</div>
                  <div className="mt-1 text-xs text-slate-400">
                    Track the latest wallet activity
                  </div>
                </button>
                <button
                  type="button"
                  onClick={handleRefresh}
                  disabled={loading}
                  className="rounded-2xl border border-white/10 bg-white/5 p-4 text-left transition hover:bg-white/10 disabled:opacity-60"
                >
                  <RefreshCw
                    className={`mb-3 h-5 w-5 text-blue-300 ${loading ? "animate-spin" : ""}`}
                  />
                  <div className="text-sm font-medium">Refresh Wallet</div>
                  <div className="mt-1 text-xs text-slate-400">
                    Sync balances and recent records
                  </div>
                </button>
              </div>
            </div>
          </div>
        </Card>

        <div className="grid gap-6">
          <Card className="border border-slate-200/70 bg-white/90 p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900/80">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-slate-400">
                  Available Balance
                </p>
                <p className="mt-2 text-3xl font-semibold text-gray-900 dark:text-white">
                  {hideBalances ? "****" : formatCurrency(walletData.availableBalance)}
                </p>
              </div>
              <div className="rounded-2xl bg-emerald-500/10 p-3 text-emerald-500">
                <CheckCircle className="h-6 w-6" />
              </div>
            </div>
            <div className="mt-4 flex items-center text-sm text-emerald-600 dark:text-emerald-400">
              <ArrowUpRight className="mr-2 h-4 w-4" />
              Ready for trading and withdrawals
            </div>
          </Card>

          <Card className="border border-slate-200/70 bg-white/90 p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900/80">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-slate-400">
                  Activity Snapshot
                </p>
                <p className="mt-2 text-2xl font-semibold text-gray-900 dark:text-white">
                  {transactions.length} Records
                </p>
              </div>
              <div className="rounded-2xl bg-amber-500/10 p-3 text-amber-500">
                <History className="h-6 w-6" />
              </div>
            </div>
            <div className="mt-4 space-y-2 text-sm text-gray-600 dark:text-slate-300">
              <div className="flex items-center justify-between">
                <span>Completed</span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {transactions.filter((tx) => tx.status === "completed").length}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span>Pending</span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {transactions.filter((tx) => tx.status === "pending").length}
                </span>
              </div>
            </div>
          </Card>
        </div>
      </div>

      <div className="space-y-6">
        <TransactionHistory />
      </div>
    </div>
  );

  const renderDeposit = () => (
    <div className="space-y-6">
      <Card className="p-6">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
          Deposit Funds
        </h3>

        {/* Payment Methods */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {depositMethods.map((method) => {
            const Icon = method.icon;
            return (
              <div
                key={method.id}
                onClick={() => setDepositMethod(method.id)}
                className={`p-4 border-2 rounded-lg cursor-pointer transition-all hover:shadow-md ${
                  depositMethod === method.id
                    ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20 shadow-md"
                    : "border-gray-200 dark:border-gray-700 hover:border-gray-300"
                }`}
              >
                <div className="flex items-center space-x-3">
                  <Icon className="w-6 h-6 text-gray-600 dark:text-gray-400" />
                  <div>
                    <div className="font-medium text-gray-900 dark:text-white">
                      {method.label}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      Withdrawal Fee
                    </div>
                    <div className="text-xs text-gray-400 dark:text-gray-500">
                      {method.fee} • {method.time}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Deposit Form */}
        <div className="max-w-md">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Currency
              </label>
              <select
                value={selectedCurrency}
                onChange={(e) => setSelectedCurrency(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="USD">USD - US Dollar</option>
                <option value="BTC">BTC - Bitcoin</option>
                <option value="ETH">ETH - Ethereum</option>
                <option value="USDT">USDT - Tether</option>
              </select>
            </div>

            <Input
              label="Amount"
              type="number"
              value={depositAmount}
              onChange={(e) => setDepositAmount(e.target.value)}
              placeholder="Enter amount"
              step="0.01"
              min="0"
            />

            {depositMethod === "bank" && (
              <Input
                label="Bank Account"
                value={bankAccount}
                onChange={(e) => setBankAccount(e.target.value)}
                placeholder="Account ending in ****1234"
              />
            )}

            {depositMethod === "card" && (
              <Input
                label="Card Number"
                value={cardNumber}
                onChange={(e) => setCardNumber(e.target.value)}
                placeholder="**** **** **** 1234"
              />
            )}

            {depositMethod === "crypto" && (
              <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                  Deposit Address ({selectedCurrency}):
                </p>
                <div className="flex items-center space-x-2">
                  <code className="bg-white dark:bg-gray-700 px-3 py-2 rounded border text-sm flex-1 break-all">
                    {selectedCurrency === "BTC"
                      ? "1A1zP1eP5QGefi2DMPTfTL5SLmv7DivfNa"
                      : selectedCurrency === "ETH"
                      ? "0x742d35Cc6634C0C532925a3b8D40C05C34D6F1f"
                      : "0x742d35Cc6634C0C532925a3b8D40C05C34D6F1f"}
                  </code>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      copyToClipboard(
                        selectedCurrency === "BTC"
                          ? "1A1zP1eP5QGefi2DMPTfTL5SLmv7DivfNa"
                          : selectedCurrency === "ETH"
                          ? "0x742d35Cc6634C0C532925a3b8D40C05C34D6F1f"
                          : "0x742d35Cc6634C0C532925a3b8D40C05C34D6F1f"
                      )
                    }
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      // Generate QR code for the address
                      setToast({
                        type: "info",
                        message: "QR code feature coming soon",
                      });
                    }}
                  >
                    <QrCode className="w-4 h-4" />
                  </Button>
                </div>
                <div className="mt-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded">
                  <div className="flex items-start space-x-2">
                    <AlertCircle className="w-4 h-4 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-yellow-700 dark:text-yellow-300">
                      <p className="font-medium mb-1">Important:</p>
                      <ul className="text-xs space-y-1">
                        <li>• Only send {selectedCurrency} to this address</li>
                        <li>
                          • Minimum deposit:{" "}
                          {selectedCurrency === "BTC"
                            ? "0.001 BTC"
                            : selectedCurrency === "ETH"
                            ? "0.01 ETH"
                            : "10 USDT"}
                        </li>
                        <li>• Deposits require network confirmations</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="pt-4">
              <Button
                onClick={() => setShowDepositModal(true)}
                disabled={!depositAmount || loading}
                loading={loading}
                className="w-full"
              >
                {depositMethod === "crypto"
                  ? "Generate Deposit Instructions"
                  : "Proceed with Deposit"}
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Deposit Instructions */}
      {depositMethod !== "crypto" && (
        <Card className="p-6">
          <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            How to {depositMethod === "bank" ? "Bank Transfer" : "Card Deposit"}
          </h4>
          <div className="space-y-3 text-sm text-gray-600 dark:text-gray-400">
            {depositMethod === "bank" ? (
              <>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 rounded-full flex items-center justify-center text-xs font-bold">
                    1
                  </div>
                  <p>Initiate transfer from your bank account</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 rounded-full flex items-center justify-center text-xs font-bold">
                    2
                  </div>
                  <p>Processing time: 1-3 business days</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 rounded-full flex items-center justify-center text-xs font-bold">
                    3
                  </div>
                  <p>Funds will appear in your wallet once cleared</p>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 rounded-full flex items-center justify-center text-xs font-bold">
                    1
                  </div>
                  <p>Enter your card details securely</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 rounded-full flex items-center justify-center text-xs font-bold">
                    2
                  </div>
                  <p>Instant processing with 2.5% fee</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 rounded-full flex items-center justify-center text-xs font-bold">
                    3
                  </div>
                  <p>Funds available immediately</p>
                </div>
              </>
            )}
          </div>
        </Card>
      )}
    </div>
  );

  const renderWithdraw = () => (
    <div className="space-y-6">
      <Card className="p-6">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
          Withdraw Funds
        </h3>

        {/* Withdrawal Methods */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {withdrawMethods.map((method) => {
            const Icon = method.icon;
            return (
              <div
                key={method.id}
                onClick={() => setWithdrawMethod(method.id)}
                className={`p-4 border-2 rounded-lg cursor-pointer transition-all hover:shadow-md ${
                  withdrawMethod === method.id
                    ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20 shadow-md"
                    : "border-gray-200 dark:border-gray-700 hover:border-gray-300"
                }`}
              >
                <div className="flex items-center space-x-3">
                  <Icon className="w-6 h-6 text-gray-600 dark:text-gray-400" />
                  <div>
                    <div className="font-medium text-gray-900 dark:text-white">
                      {method.label}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      Fee: {method.fee} • {method.time}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Withdrawal Form */}
        <div className="max-w-md">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Currency
              </label>
              <select
                value={selectedCurrency}
                onChange={(e) => {
                  const nextCurrency = e.target.value;
                  setSelectedCurrency(nextCurrency);
                  setSelectedWithdrawOption(
                    getMatchingWithdrawOptionId(depositOptions, nextCurrency)
                  );
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                {depositOptions.map((option) => (
                  <option key={option.id} value={option.symbol}>
                    {option.label} - Available:{" "}
                    {formatCurrency(virtualPortfolioBalance)}
                  </option>
                ))}
              </select>
            </div>

            <Input
              label="Amount"
              type="number"
              value={withdrawAmount}
              onChange={(e) => setWithdrawAmount(e.target.value)}
              placeholder="Enter amount"
              step="0.01"
              min="0"
              max={virtualPortfolioBalance || 0}
            />

            {withdrawMethod === "bank" && (
              <Input
                label="Bank Account"
                value={bankAccount}
                onChange={(e) => setBankAccount(e.target.value)}
                placeholder="Select saved account or add new"
              />
            )}

            {withdrawMethod === "crypto" && (
              <div className="space-y-4">
                <Input
                  label={`${selectedCurrency} Wallet Address`}
                  value={withdrawAddress}
                  onChange={(e) => setWithdrawAddress(e.target.value)}
                  placeholder={`Enter ${selectedCurrency} wallet address`}
                />
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded">
                  <div className="flex items-start space-x-2">
                    <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-red-700 dark:text-red-300">
                      <p className="font-medium mb-1">Warning:</p>
                      <p>
                        Double-check the wallet address. Cryptocurrency
                        transactions are irreversible.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Fee Calculation */}
            {withdrawAmount && (
              <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div className="flex justify-between text-sm mb-2">
                  <span>Withdrawal Amount:</span>
                  <span>
                    {selectedCurrency === "USD"
                      ? formatCurrency(parseFloat(withdrawAmount))
                      : formatNumber(parseFloat(withdrawAmount))}
                  </span>
                </div>
                <div className="flex justify-between text-sm mb-2">
                    <span>Withdrawal Fee:</span>
                    <span>Network fee</span>
                  </div>
                  <div className="border-t pt-2 flex justify-between font-medium">
                    <span>You'll Receive:</span>
                    <span>
                    {selectedCurrency === "USD"
                      ? `${formatCurrency(parseFloat(withdrawAmount))} (minus network fee)`
                      : `${formatNumber(
                          parseFloat(withdrawAmount)
                        )} ${selectedCurrency} (minus network fee)`}
                    </span>
                  </div>
              </div>
            )}

            <div className="pt-4">
              <Button
                onClick={openWithdrawConfirmation}
                disabled={
                  !withdrawAmount ||
                  loading ||
                  (withdrawMethod === "crypto" && !withdrawAddress)
                }
                loading={loading}
                className="w-full"
                variant="outline"
              >
                Review Withdrawal
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Withdrawal Limits */}
      <Card className="p-6">
        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Withdrawal Limits
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">
              Daily Limit
            </div>
            <div className="text-xl font-bold text-gray-900 dark:text-white">
              $50,000
            </div>
            <div className="text-sm text-blue-600 dark:text-blue-400">
              $15,430 used today
            </div>
          </div>
          <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">
              Monthly Limit
            </div>
            <div className="text-xl font-bold text-gray-900 dark:text-white">
              $500,000
            </div>
            <div className="text-sm text-green-600 dark:text-green-400">
              $45,230 used this month
            </div>
          </div>
        </div>
      </Card>
    </div>
  );

  const renderHistory = () => (
    <div className="space-y-6">
      <div className="flex justify-start">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setActiveTab("overview")}
          className="inline-flex items-center"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Wallet
        </Button>
      </div>
      <TransactionHistory />
    </div>
  );

  return (
    <DashboardLayout>
      <div className="space-y-5 p-4 sm:space-y-6 sm:p-6 lg:p-8">
        {/* Header */}
        <div className="flex flex-col items-center gap-4 text-center sm:flex-row sm:items-start sm:justify-between sm:text-left">
          <div className="mb-4">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Wallet
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Manage your funds and transaction history
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setHideBalances(!hideBalances)}
            >
              {hideBalances ? (
                <Eye className="w-4 h-4" />
              ) : (
                <EyeOff className="w-4 h-4" />
              )}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={loading}
            >
              <RefreshCw
                className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
              />
            </Button>
          </div>
        </div>
        {/* Tabs */}
        <Tabs
          tabs={tabsData}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />

        {/* Tab Content */}
        {activeTab === "overview" && renderOverview()}
        {activeTab === "deposit" && renderDeposit()}
        {activeTab === "withdraw" && renderWithdraw()}
        {activeTab === "history" && renderHistory()}

        {/* Deposit Modal */}
        <Modal
          isOpen={showDepositModal}
          onClose={() => {
            setShowDepositModal(false);
            setSelectedDepositOption("bitcoin");
          }}
          title="Deposit Crypto"
          size="lg"
        >
          <div className="space-y-5 rounded-2xl bg-slate-950 px-1 py-1 text-slate-100">
            <p className="text-sm text-slate-300">
              Choose a deposit network and send funds to the matching address only.
            </p>

            <div className="grid gap-3 md:grid-cols-3">
              {depositOptions.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setSelectedDepositOption(option.id)}
                  className={`rounded-xl border p-4 text-left transition ${
                    selectedDepositOption === option.id
                      ? "border-cyan-400 bg-slate-900"
                      : "border-slate-800 bg-slate-950 hover:border-slate-600"
                  }`}
                >
                  <div className="text-sm font-semibold text-slate-100">
                    {option.label}
                  </div>
                  <div className="mt-1 text-xs text-slate-400">
                    Network: {option.network}
                  </div>
                </button>
              ))}
            </div>

            <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
              <div className="mb-2 flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold text-slate-100">
                    {activeDepositOption.label}
                  </div>
                  <div className="text-xs text-slate-400">
                    Wallet Address
                  </div>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(activeDepositOption.address);
                    setToast({
                      type: "success",
                      message: `${activeDepositOption.label} address copied`,
                    });
                  }}
                >
                  <Copy className="mr-2 h-4 w-4" />
                  Copy
                </Button>
              </div>

              <div className="break-all rounded-lg bg-slate-950 px-3 py-3 font-mono text-sm text-slate-100">
                {activeDepositOption.address}
              </div>

              <div className="mt-4">
                <label className="mb-2 block text-sm font-medium text-slate-300">
                  Deposit Amount
                </label>
                <div className="flex items-center overflow-hidden rounded-lg border border-slate-700 bg-slate-950 transition focus-within:border-cyan-400 focus-within:ring-2 focus-within:ring-cyan-400/20">
                  <input
                    type="text"
                    inputMode="decimal"
                    value={depositAmount === "" ? "0" : depositAmount}
                    onChange={(e) => {
                      const sanitizedValue = e.target.value.replace(/[^0-9.]/g, "");
                      setDepositAmount(sanitizedValue);
                    }}
                    className="w-full bg-transparent px-3 py-3 text-sm text-slate-100 outline-none placeholder:text-slate-500"
                    placeholder="0"
                  />
                  <span className="border-l border-slate-800 px-3 text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                    USD
                  </span>
                </div>
              </div>

              <div className="mt-4 flex items-center justify-center rounded-lg border border-dashed border-slate-700 bg-slate-950 p-6">
                {activeDepositOption.qrImageUrl ? (
                  <img
                    src={activeDepositOption.qrImageUrl}
                    alt={`${activeDepositOption.label} QR code`}
                    className="max-h-52 w-full max-w-[220px] rounded-lg object-contain"
                  />
                ) : (
                  <div className="text-center">
                    <QrCode className="mx-auto mb-2 h-10 w-10 text-slate-400" />
                    <div className="text-sm text-slate-400">
                      QR code will display once uploaded from backend for {activeDepositOption.label}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="rounded-xl border border-amber-700/40 bg-amber-950/40 p-4 text-sm text-amber-200">
              Send only {activeDepositOption.label} via {activeDepositOption.network}. Sending assets on the wrong network may result in permanent loss.
            </div>
          </div>
        </Modal>

        {/* Withdraw Modal */}
        <Modal
          isOpen={showWithdrawModal}
            onClose={() => {
              window.clearTimeout(withdrawalToastTimeoutRef.current);
              setShowWithdrawModal(false);
              setSelectedWithdrawOption("bitcoin");
              setWithdrawalFlowStatus("idle");
              withdrawalSubmitLockRef.current = false;
              activeWithdrawalActionKeyRef.current = null;
            }}
          title="Confirm Withdrawal"
        >
          <div className="space-y-4 rounded-2xl bg-slate-950 px-1 py-1 text-slate-100">
            <div className="grid gap-3 md:grid-cols-3">
              {depositOptions.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => {
                    setWithdrawalFlowStatus("idle");
                    setSelectedWithdrawOption(option.id);
                    setSelectedCurrency(option.symbol);
                    setWithdrawMethod("crypto");
                  }}
                  className={`rounded-xl border p-4 text-left transition ${
                    selectedWithdrawOption === option.id
                      ? "border-cyan-400 bg-slate-900"
                      : "border-slate-800 bg-slate-950 hover:border-slate-600"
                  }`}
                >
                  <div className="text-sm font-semibold text-slate-100">
                    {option.label}
                  </div>
                  <div className="mt-1 text-xs text-slate-400">
                    Network: {option.network}
                  </div>
                </button>
              ))}
            </div>

            <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm text-slate-400">
                    Withdrawal Address
                  </label>
                  <Input
                    value={withdrawAddress}
                    onChange={(e) => {
                      setWithdrawalFlowStatus("idle");
                      setWithdrawAddress(e.target.value);
                    }}
                    placeholder={`Enter ${
                      activeWithdrawOption?.label || "crypto"
                    } wallet address`}
                    className="border-slate-700 bg-slate-950 text-slate-100 placeholder:text-slate-500"
                  />
                </div>
                <div>
                  <label className="mb-2 block text-sm text-slate-400">
                    Withdrawal Amount
                  </label>
                  <Input
                    type="number"
                    value={withdrawAmount}
                    onChange={(e) => {
                      setWithdrawalFlowStatus("idle");
                      setWithdrawAmount(e.target.value);
                    }}
                    placeholder="Enter amount"
                    className="border-slate-700 bg-slate-950 text-slate-100 placeholder:text-slate-500"
                  />
                </div>
                <div>
                  <span className="text-slate-400">Currency:</span>
                  <p className="font-medium text-slate-100">
                    {activeWithdrawSymbol}
                  </p>
                </div>
                <div>
                  <span className="text-slate-400">Withdrawal Fee:</span>
                  <p className="font-medium text-slate-100">Network fee</p>
                </div>
              </div>
              {withdrawMethod === "crypto" && withdrawAddress && (
                <div className="mt-3 border-t border-slate-800 pt-3">
                  <span className="text-sm text-slate-400">
                    Destination:
                  </span>
                  <p className="mt-1 break-all rounded bg-slate-950 p-2 font-mono text-xs text-slate-100">
                    {withdrawAddress}
                  </p>
                </div>
              )}
              {withdrawAmount && (
                <div className="mt-3 border-t border-slate-800 pt-3">
                  <span className="text-sm text-slate-400">
                    You Will Withdraw:
                  </span>
                  <p className="mt-1 font-medium text-slate-100">
                    {activeWithdrawSymbol === "USD"
                      ? formatCurrency(parseFloat(withdrawAmount || 0))
                      : `${formatNumber(parseFloat(withdrawAmount || 0))} ${activeWithdrawSymbol}`}
                  </p>
                </div>
              )}
            </div>

            <div className="rounded-xl border border-rose-700/40 bg-rose-950/30 p-3">
              <div className="flex items-start space-x-2">
                <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-rose-300" />
                <div className="text-sm text-rose-200">
                  <p className="font-medium">
                    Please verify all details carefully.
                  </p>
                  <p>This action cannot be undone once confirmed.</p>
                </div>
              </div>
            </div>
            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowWithdrawModal(false);
                  setWithdrawalFlowStatus("idle");
                  window.clearTimeout(withdrawalToastTimeoutRef.current);
                  withdrawalSubmitLockRef.current = false;
                  activeWithdrawalActionKeyRef.current = null;
                }}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleWithdraw}
                loading={loading}
                className="flex-1"
                variant={withdrawalFlowStatus === "approved" ? "success" : "danger"}
                disabled={withdrawalFlowStatus === "approved"}
              >
                {withdrawalFlowStatus === "processing"
                  ? "Processing Withdrawal"
                  : withdrawalFlowStatus === "approved"
                  ? "Withdrawal Approved"
                  : "Confirm Withdrawal"}
              </Button>
            </div>
          </div>
        </Modal>

        {/* Toast Notifications */}
        {toast && (
          <Toast
            type={toast.type}
            message={toast.message}
            onClose={() => setToast(null)}
          />
        )}
      </div>
    </DashboardLayout>
  );
};

export default Wallet;
